"use client"

import { useEffect, useId, useMemo, useState, type ReactNode } from "react"
import { useLocale } from "next-intl"
import { useTranslations } from "@/lib/intl/use-translations"
import { CalculatorBookingCta } from "@/components/tools/calculator-booking-cta"
import { buttonVariants } from "@/components/ui/button"
import { bmrCalculatorKeys as k } from "@/i18n/bmr-calculator-keys"
import { formatInteger, formatNumber } from "@/lib/locale-number-format"
import { track } from "@/lib/analytics"
import { cn } from "@/lib/utils"
import {
  ACTIVITY_BY_ID,
  ACTIVITY_LEVELS,
  DEFAULT_ACTIVITY,
  dailyCalories,
  harrisBenedict,
  isPlausible,
  mifflinStJeor,
  roundCalories,
  type ActivityLevel,
  type BmrSex,
} from "@/lib/bmr"

/**
 * The interactive part of /tools/bmr-calculator.
 *
 * One centred column: the page intro, then the form, then the result panel,
 * which does not exist until Calculate has run. One component owns all three
 * because the form and the result share state; the `intro` slot is what keeps
 * the h1 in the client file, where the page's structure is meant to stay
 * readable.
 *
 * Deliberately framer-motion-free: the widget is always mounted, so any
 * animation here would have to clear the `<AnimatePresence>` + cacheComponents
 * trap (see `/animation-cache-trap`).
 *
 * Nothing is submitted, stored or tracked: the arithmetic happens in the
 * browser and never leaves it. That matters more here than on the BMI page,
 * because this form asks for age and sex — and note the page no longer SAYS
 * so anywhere, since the hero bullet that carried the promise was removed.
 * Worth re-stating on the surface if anyone asks where the data goes.
 */

interface BmrResult {
  /** Mifflin-St Jeor, rounded to the kcal the reader sees. */
  bmr: number
  /** Revised Harris-Benedict, shown for comparison. */
  harris: number
}

const inputClasses =
  "type-body-lg w-full rounded-lg border border-neutral-200 bg-white px-4 py-3 text-neutral-900 tabular-nums focus-visible:border-transparent focus-visible:ring-2 focus-visible:ring-aubergine-400 focus-visible:outline-none"

const unitSuffixClasses =
  "type-body-md pointer-events-none absolute inset-y-0 right-4 flex items-center text-neutral-500"

const labelClasses = "type-label-md block text-neutral-700 normal-case"

/**
 * NaN for a blank or unparseable field. `Number("")` is 0, which would read a
 * still-empty box as a real zero and let the form submit on nothing.
 */
function parseField(value: string): number {
  const trimmed = value.trim().replace(",", ".")
  if (trimmed === "") return NaN
  return Number(trimmed)
}

/**
 * `as const`, and at module scope. Annotating `labelKey` as `string` widens it
 * out of the catalog's key union and next-intl's typed `t()` rejects it - the
 * same mistake that reddened the BMI page's first CI run.
 */
const SEX_OPTIONS = [
  { value: "female", labelKey: k.calcSexFemale },
  { value: "male", labelKey: k.calcSexMale },
] as const

export function BmrCalculatorWidget({ intro }: { intro?: ReactNode }) {
  const t = useTranslations("bmrCalculator")
  // Every kcal figure and the activity factor are rendered here rather than
  // read from the catalog, so their separators have to be chosen explicitly.
  // A German reader parses "1,730" as one point seven three.
  const locale = useLocale()
  const fieldId = useId()

  /**
   * Flipped in an effect, and stamped onto the root as `data-hydrated`.
   *
   * Not decoration: the inputs are controlled, so anything typed into them
   * before React owns them is silently discarded, and an e2e test has no other
   * way to know when that moment has passed. On the BMI page a blind retry
   * loop kept flaking on a loaded CI runner until this signal existed. One
   * boolean, renders nothing.
   */
  const [hydrated, setHydrated] = useState(false)
  useEffect(() => {
    setHydrated(true)
  }, [])

  const [sex, setSex] = useState<BmrSex>("female")
  const [age, setAge] = useState("")
  const [heightCm, setHeightCm] = useState("")
  const [weightKg, setWeightKg] = useState("")

  /**
   * The result only exists after Calculate, and any change to a body field
   * drops it — a stale figure sitting beside edited inputs is the one thing
   * this layout must not show.
   *
   * The activity level is deliberately NOT in that set. It lives in the result
   * panel and only scales the daily total, so changing it re-derives the total
   * from the BMR already calculated rather than invalidating it. The rule is
   * therefore per-panel and easy to state: everything the left-hand form owns
   * needs Calculate, the right-hand control adjusts what is already there.
   * Without that split, comparing the five activity levels would mean pressing
   * Calculate five times.
   */
  const [result, setResult] = useState<BmrResult | null>(null)
  const [outOfRange, setOutOfRange] = useState(false)
  const [activity, setActivity] = useState<ActivityLevel>(DEFAULT_ACTIVITY)

  /** Every body-field setter goes through this. */
  function invalidate() {
    setResult(null)
    setOutOfRange(false)
  }

  const hasInput =
    age.trim() !== "" || heightCm.trim() !== "" || weightKg.trim() !== ""

  /**
   * Age + height in cm + weight in kg, or null while a required field is
   * blank.
   */
  const input = useMemo(() => {
    const years = parseField(age)
    if (!Number.isFinite(years)) return null
    const cm = parseField(heightCm)
    const kg = parseField(weightKg)
    if (!Number.isFinite(cm) || !Number.isFinite(kg)) return null
    return { sex, age: years, heightCm: cm, weightKg: kg }
  }, [sex, age, heightCm, weightKg])

  /**
   * Plausibility is checked here rather than by disabling the button: a
   * disabled control with no explanation leaves the reader guessing, so the
   * button enables as soon as the form is complete and an implausible answer
   * gets a message instead.
   */
  function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (!input) return
    if (!isPlausible(input)) {
      setResult(null)
      setOutOfRange(true)
      track.calculatorResult({
        calculator: "bmr",
        outcome: "rejected",
        reason: "outOfRange",
      })
      return
    }
    setOutOfRange(false)
    track.calculatorResult({ calculator: "bmr", outcome: "result" })
    setResult({
      bmr: roundCalories(mifflinStJeor(input)),
      harris: roundCalories(harrisBenedict(input)),
    })
  }

  function reset() {
    setAge("")
    setHeightCm("")
    setWeightKg("")
    invalidate()
  }

  /**
   * Derived, not stored — see the note on `result`. Takes the ROUNDED BMR so
   * the arithmetic on screen is reproducible by hand.
   */
  const tdee = result !== null ? dailyCalories(result.bmr, activity) : null

  return (
    <div
      data-testid="bmr-calculator"
      data-hydrated={hydrated ? "true" : undefined}
      /* One centred column at the prose measure, lining up with the
         sections below it. The result no longer needs a column of its
         own: it does not exist until Calculate has run. */
      className="mx-auto w-full max-w-[760px]"
    >
      {/* ── Intro ── */}
      <div className="text-center">{intro}</div>

      {/* ── Form ── */}
      {/*
        `noValidate` is load-bearing, exactly as it is on the four other
        calculators. The number inputs carry min/max that mirror this
        widget's own plausibility bounds, so native constraint validation
        refuses the submit and pops a browser-language bubble before
        `handleSubmit` runs — `bmr_calcOutOfRange` could never render. It hid
        behind the always-mounted panel's empty-state line; with that panel
        gone until Calculate succeeds it would leave no answer at all.
      */}
      <form onSubmit={handleSubmit} noValidate className="mt-10">
        <h2 className="type-title-lg text-center text-neutral-900">
          {t(k.calcTitle)}
        </h2>

        <div className="mt-6 space-y-4">
          {/* Sex — a real radiogroup, styled as a segmented control. */}
          <div role="radiogroup" aria-labelledby={`${fieldId}-sex-label`}>
            <span id={`${fieldId}-sex-label`} className={labelClasses}>
              {t(k.calcSexLabel)}
            </span>
            <div className="mt-2 flex gap-2">
              {SEX_OPTIONS.map((option) => {
                const selected = sex === option.value
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    data-testid={`bmr-sex-${option.value}`}
                    onClick={() => {
                      setSex(option.value)
                      invalidate()
                    }}
                    className={cn(
                      "type-title-sm flex-1 cursor-pointer rounded-full px-4 py-2.5 transition-colors",
                      selected
                        ? "bg-aubergine-800 text-white"
                        : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
                    )}
                  >
                    {t(option.labelKey)}
                  </button>
                )
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor={`${fieldId}-age`} className={labelClasses}>
                {t(k.calcAgeLabel)}
              </label>
              <div className="relative mt-2">
                <input
                  id={`${fieldId}-age`}
                  data-testid="bmr-age"
                  type="number"
                  inputMode="numeric"
                  min={15}
                  max={100}
                  step="1"
                  autoComplete="off"
                  placeholder="40"
                  value={age}
                  onChange={(e) => {
                    setAge(e.target.value)
                    invalidate()
                  }}
                  className={cn(inputClasses, "pr-20")}
                />
                <span className={unitSuffixClasses}>{t(k.calcUnitYears)}</span>
              </div>
            </div>

            <div>
              <label htmlFor={`${fieldId}-height-cm`} className={labelClasses}>
                {t(k.calcHeightLabel)}
              </label>
              <div className="relative mt-2">
                <input
                  id={`${fieldId}-height-cm`}
                  data-testid="bmr-height-cm"
                  type="number"
                  inputMode="decimal"
                  min={90}
                  max={250}
                  step="0.5"
                  autoComplete="off"
                  placeholder="180"
                  value={heightCm}
                  onChange={(e) => {
                    setHeightCm(e.target.value)
                    invalidate()
                  }}
                  className={cn(inputClasses, "pr-14")}
                />
                <span className={unitSuffixClasses}>{t(k.calcUnitCm)}</span>
              </div>
            </div>
          </div>

          <div>
            <label htmlFor={`${fieldId}-weight-kg`} className={labelClasses}>
              {t(k.calcWeightLabel)}
            </label>
            <div className="relative mt-2">
              <input
                id={`${fieldId}-weight-kg`}
                data-testid="bmr-weight-kg"
                type="number"
                inputMode="decimal"
                min={20}
                max={400}
                step="0.1"
                autoComplete="off"
                placeholder="80"
                value={weightKg}
                onChange={(e) => {
                  setWeightKg(e.target.value)
                  invalidate()
                }}
                className={cn(inputClasses, "pr-14")}
              />
              <span className={unitSuffixClasses}>{t(k.calcUnitKg)}</span>
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center gap-4">
          {/*
            `buttonVariants` on a bare <button> rather than <Button>: that is
            the idiom already used for the membership CTA in this page's
            client, and it keeps `data-testid` off a custom component's prop
            type. The variant's `disabled:` classes cover the greyed state.
          */}
          <button
            type="submit"
            data-testid="bmr-submit"
            disabled={input === null}
            className={cn(buttonVariants({ variant: "primary" }), "flex-1")}
          >
            {t(k.calcSubmit)}
          </button>
          {hasInput && (
            <button
              type="button"
              onClick={reset}
              className="type-title-sm shrink-0 cursor-pointer text-neutral-500 underline underline-offset-4 transition-colors hover:text-neutral-900"
            >
              {t(k.calcReset)}
            </button>
          )}
        </div>

        {/*
          The plausibility message used to be the result panel's status line.
          With that panel gone until Calculate succeeds, it belongs beside the
          control that produced it.
        */}
        {outOfRange && (
          <p
            data-testid="bmr-status"
            className="type-body-md text-terracotta-700 mt-4 text-center"
          >
            {t(k.calcOutOfRange)}
          </p>
        )}
      </form>

      {/* ── Result ── */}
      {/*
        The live region is mounted from the start so the panel is
        announced when it arrives; the panel itself only exists once
        Calculate has produced a figure, which is the point of the
        layout.
      */}
      <div aria-live="polite">
        {result !== null && tdee !== null && (
          <div
            data-testid="bmr-result"
            className="mt-8 rounded-[16px] bg-white p-6 md:p-8"
          >
            <>
              <p className="type-label-md text-neutral-500 normal-case">
                {t(k.calcBmrLabel)}
              </p>
              <p className="mt-1 flex flex-wrap items-baseline gap-2">
                <span
                  data-testid="bmr-value"
                  className="type-display-md text-aubergine-800 tabular-nums"
                >
                  {formatInteger(result.bmr, locale)}
                </span>
                <span className="type-body-lg text-neutral-500">
                  {t(k.calcKcalPerDay)}
                </span>
              </p>

              <div className="mt-6 border-t border-neutral-200 pt-5">
                <label
                  htmlFor={`${fieldId}-activity`}
                  className="type-label-md block text-neutral-500 normal-case"
                >
                  {t(k.calcActivityLabel)}
                </label>
                {/* Labels only; the full descriptions live in the reference
                    table further down the page. */}
                <select
                  id={`${fieldId}-activity`}
                  data-testid="bmr-activity"
                  value={activity}
                  onChange={(e) => setActivity(e.target.value as ActivityLevel)}
                  className={cn(inputClasses, "mt-2 cursor-pointer pr-10")}
                >
                  {ACTIVITY_LEVELS.map((level) => (
                    <option key={level.id} value={level.id}>
                      {t(level.labelKey)}
                    </option>
                  ))}
                </select>

                <p className="type-label-md mt-5 text-neutral-500 normal-case">
                  {t(k.calcTdeeLabel)}
                </p>
                <p className="mt-1 flex flex-wrap items-baseline gap-2">
                  <span
                    data-testid="bmr-tdee"
                    className="type-headline-md text-neutral-900 tabular-nums"
                  >
                    {formatInteger(tdee, locale)}
                  </span>
                  <span className="type-body-md text-neutral-500">
                    {t(k.calcKcalPerDay)}
                  </span>
                  <span
                    data-testid="bmr-tdee-factor"
                    className="type-body-md text-neutral-400 tabular-nums"
                  >
                    {`x ${formatNumber(ACTIVITY_BY_ID[activity].factor, locale)}`}
                  </span>
                </p>
                <p className="type-body-sm mt-2 text-neutral-500">
                  {t(k.calcTdeeHint)}
                </p>
              </div>

              <div className="mt-6 border-t border-neutral-200 pt-5">
                <p className="type-label-md text-neutral-500 normal-case">
                  {t(k.calcCompareLabel)}
                </p>
                <p className="mt-1 flex flex-wrap items-baseline gap-2">
                  <span
                    data-testid="bmr-harris"
                    className="type-title-lg text-neutral-700 tabular-nums"
                  >
                    {formatInteger(result.harris, locale)}
                  </span>
                  <span className="type-body-md text-neutral-500">
                    {t(k.calcKcalPerDay)}
                  </span>
                </p>
                <p className="type-body-sm mt-2 text-neutral-500">
                  {t(k.calcCompareHint)}
                </p>
              </div>
            </>
            {/*
              Check-up hand-off. Inside the result panel and gated on `result`, so
              it is the reward for pressing Calculate rather than another CTA
              sitting on the page from the start. It books the market's blood
              check-up directly (CalculatorBookingCta) rather than sending the
              reader through the quiz first, and it does NOT claim the booking
              reads this result.
            */}
            <div className="bg-aubergine-50 mt-6 rounded-lg p-5">
              <h3 className="type-title-md text-neutral-900">
                {t(k.quizHeading)}
              </h3>
              <p className="type-body-md mt-2 text-neutral-600">
                {t(k.quizBody)}
              </p>
              <CalculatorBookingCta calculator="bmr">
                {t(k.quizCta)}
              </CalculatorBookingCta>
            </div>
          </div>
        )}
      </div>

      {/* The caveat is about the tool, not the answer, so it stays on
          the page whether or not anything has been calculated. */}
      <p className="type-body-sm mx-auto mt-8 max-w-[600px] text-center text-neutral-500">
        {t(k.calcDisclaimer)}
      </p>
    </div>
  )
}
